import { type ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  EditButton,
  ReferenceField,
  Show,
  TextField,
  useRecordContext,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { CategoryStatusChip } from './CategoryList';

/**
 * Category detail (Show) — read-only view; the parent is shown read-only (the
 * tree structure is not edited here, D-categories-light). The Edit affordance is
 * gated `categories:write`. Categories are market-agnostic, so there is no region
 * guard.
 */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const CategoryShowLayout = () => {
  const record = useRecordContext();
  const { role, accessibleRegions } = useAccessContext();
  if (!record) return null;

  const canEdit = hasAccess(
    { role, accessibleRegions },
    { capability: 'categories:write' },
  );

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Typography variant="h5" component="h1">
              {record.name as string}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
              <CategoryStatusChip />
              {canEdit && <EditButton label="Edit" />}
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Field label="Parent category">
            {record.parentId != null ? (
              <ReferenceField
                source="parentId"
                reference="categories"
                link={false}
                emptyText="—"
              >
                <TextField source="name" />
              </ReferenceField>
            ) : (
              'Top-level category'
            )}
          </Field>
          <Field label="Slug">{record.slug as string}</Field>
        </CardContent>
      </Card>
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const CategoryShowView = () => (
  <Show title="Category" actions={false} resource="categories">
    <CategoryShowLayout />
  </Show>
);

/** Gated entry point registered as the `categories` Resource's show. */
export const CategoryShow = () => (
  <RequireAccess capability="categories:read">
    <CategoryShowView />
  </RequireAccess>
);
